import { useState } from "react";
import { Filter } from "lucide-react";
import parsePhoneNumber from "libphonenumber-js";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatPhoneAsYouType } from "@/lib/validation/phoneNumber";
import { Label } from "@/components/ui/label";
import { Combobox } from "@/components/ui/combobox";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import { useListMinimalUsers } from "@/lib/api/users";
import { useListCustomers } from "@/lib/api/customer";
import type { GoToFilterType, GoToEntitySide, GoToMessagesParams } from "@/lib/api/gotoChat";

// ── Option constants ────────────────────────────────────────────────────────

const filterTypeOptions: { value: GoToFilterType; label: string }[] = [
  // { value: "all", label: "All Messages" },
  { value: "by_number", label: "By Phone Number" },
  { value: "conversation", label: "Conversation (Two Numbers)" },
  // { value: "owner_conversation", label: "Owner Conversation" },
  // { value: "by_entity", label: "By Entity" },
];

const DEFAULT_FILTER_TYPE = filterTypeOptions[0].value;

const entityTypeOptions: { value: "user" | "customer"; label: string }[] = [
  { value: "user", label: "User" },
  { value: "customer", label: "Customer" },
];

const entitySideOptions: { value: GoToEntitySide; label: string }[] = [
  { value: "any", label: "Any Side" },
  { value: "from", label: "From" },
  { value: "to", label: "To" },
];

const pageSizeOptions: { value: string; label: string }[] = [
  { value: "25", label: "25" },
  { value: "50", label: "50" },
  { value: "100", label: "100" },
];

// ── Phone input with as-you-type formatting ─────────────────────────────────

/**
 * Convert a display-formatted (or raw) phone value to E.164 for the API.
 * Prepends +1 if the user didn't include a country code.
 */
function toE164(displayValue: string): string {
  if (!displayValue.trim()) return "";
  const parsed = parsePhoneNumber(displayValue, "US");
  if (parsed?.isValid()) return parsed.format("E.164");
  // Fallback: strip non-digits and prepend +1
  const digits = displayValue.replace(/\D/g, "");
  if (!digits) return "";
  if (digits.startsWith("1") && digits.length === 11) return `+${digits}`;
  return `+1${digits}`;
}

function PhoneInput({
  value,
  onValueChange,
  placeholder,
}: {
  value: string;
  onValueChange: (formatted: string) => void;
  placeholder?: string;
}) {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneAsYouType(e.target.value);
    onValueChange(formatted);
  };

  return (
    <Input
      value={value}
      onChange={handleChange}
      placeholder={placeholder || "(555) 123-4567"}
    />
  );
}

// ── Types ───────────────────────────────────────────────────────────────────

export interface GoToChatFilterValues {
  filter_type: GoToFilterType;
  date_from?: string;
  date_to?: string;
  pageSize: number;

  // by_number
  phone_number: string;

  // conversation
  number_a: string;
  number_b: string;

  // owner_conversation
  owner_number: string;
  counterparty_number: string;

  // by_entity
  entity_type: "user" | "customer";
  entity_id: string;
  side: GoToEntitySide;
}

interface GoToChatFiltersProps {
  values: GoToChatFilterValues;
  onChange: (values: GoToChatFilterValues) => void;
  onApply: () => void;
}

// ── Component ───────────────────────────────────────────────────────────────

export function GoToChatFilters({ values, onChange, onApply }: GoToChatFiltersProps) {
  const [open, setOpen] = useState(false);

  const minimalUsersResult = useListMinimalUsers();
  const users = minimalUsersResult?.data?.data || [];

  const customersResult = useListCustomers({ minimal: true });
  const customers = customersResult?.data?.data || [];

  const userOptions = users.map((u: { id: string; name: string }) => ({ value: u.id, label: u.name }));
  const customerOptions = customers.map((c: { id: string; long_name: string }) => ({ value: c.id, label: c.long_name }));

  const update = (patch: Partial<GoToChatFilterValues>) => {
    onChange({ ...values, ...patch });
  };

  const handleApply = () => {
    onApply();
    setOpen(false);
  };

  const dateRange = {
    from: values.date_from ? new Date(values.date_from) : undefined,
    to: values.date_to ? new Date(values.date_to) : undefined,
  };

  const handleDateRangeChange = (range: { from?: Date; to?: Date }) => {
    update({
      date_from: range.from ? range.from.toISOString() : undefined,
      date_to: range.to ? range.to.toISOString() : undefined,
    });
  };

  const filterContent = (
    <div className="flex flex-col gap-4">
      {/* Filter Type */}
      <div className="flex flex-col gap-1.5">
        <Label>Filter Type</Label>
        <Combobox
          dwimOnChange
          options={filterTypeOptions}
          value={values.filter_type}
          onValueChange={(v) => update({ filter_type: (v || DEFAULT_FILTER_TYPE) as GoToFilterType })}
          className="w-full"
          placeholder="Select filter type"
        />
      </div>

      {/* Date Range */}
      <div className="flex flex-col gap-1.5">
        <Label>Date Range</Label>
        <DateRangePicker
          value={dateRange}
          onChange={handleDateRangeChange}
        />
      </div>

      {/* ── by_number fields ─────────────────────────────────────────── */}
      {values.filter_type === "by_number" && (
        <div className="flex flex-col gap-1.5">
          <Label>Phone Number</Label>
          <PhoneInput
            value={values.phone_number}
            onValueChange={(v) => update({ phone_number: v })}
          />
        </div>
      )}

      {/* ── conversation fields ──────────────────────────────────────── */}
      {values.filter_type === "conversation" && (
        <>
          <div className="flex flex-col gap-1.5">
            <Label>Number A</Label>
            <PhoneInput
              value={values.number_a}
              onValueChange={(v) => update({ number_a: v })}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Number B</Label>
            <PhoneInput
              value={values.number_b}
              onValueChange={(v) => update({ number_b: v })}
            />
          </div>
        </>
      )}

      {/* ── owner_conversation fields ────────────────────────────────── */}
      {values.filter_type === "owner_conversation" && (
        <>
          <div className="flex flex-col gap-1.5">
            <Label>Owner Number</Label>
            <PhoneInput
              value={values.owner_number}
              onValueChange={(v) => update({ owner_number: v })}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Counterparty Number</Label>
            <PhoneInput
              value={values.counterparty_number}
              onValueChange={(v) => update({ counterparty_number: v })}
            />
          </div>
        </>
      )}

      {/* ── by_entity fields ─────────────────────────────────────────── */}
      {values.filter_type === "by_entity" && (
        <>
          <div className="flex flex-col gap-1.5">
            <Label>Entity Type</Label>
            <Combobox
              dwimOnChange
              options={entityTypeOptions}
              value={values.entity_type}
              onValueChange={(v) =>
                update({
                  entity_type: (v || "user") as "user" | "customer",
                  entity_id: "",
                })
              }
              className="w-full"
              placeholder="Select entity type"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>{values.entity_type === "user" ? "User" : "Customer"}</Label>
            <Combobox
              dwimOnChange
              virtualizeList
              options={values.entity_type === "user" ? userOptions : customerOptions}
              value={values.entity_id}
              onValueChange={(v) => update({ entity_id: v })}
              className="w-full"
              placeholder={`Select ${values.entity_type}`}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Side</Label>
            <Combobox
              dwimOnChange
              options={entitySideOptions}
              value={values.side}
              onValueChange={(v) => update({ side: (v || "any") as GoToEntitySide })}
              className="w-full"
              placeholder="Select side"
            />
          </div>
        </>
      )}

      {/* Apply */}
      <Button onClick={handleApply} className="w-full mt-2">
        Apply Filters
      </Button>
    </div>
  );

  return (
    <>
      {/* Mobile: Sheet trigger — always rendered, hidden via CSS on md+ */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <Button variant="outline" size="sm" className="gap-2 md:hidden">
            <Filter className="h-4 w-4" />
            Filters
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="w-[300px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Filters</SheetTitle>
          </SheetHeader>
          <div className="mt-4">
            {filterContent}
          </div>
        </SheetContent>
      </Sheet>

      {/* Desktop: inline sidebar */}
      <div className="hidden md:flex flex-col w-[280px] shrink-0 border rounded-lg p-4 overflow-y-auto">
        <h3 className="font-semibold text-sm mb-4">Filters</h3>
        {filterContent}
      </div>
    </>
  );
}

/**
 * Build the API params from the filter values + a page number.
 */
export function buildGoToParams(
  filters: GoToChatFilterValues,
  page: number
): GoToMessagesParams {
  const params: GoToMessagesParams = {
    filter_type: filters.filter_type,
    page,
    pageSize: filters.pageSize,
    date_from: filters.date_from,
    date_to: filters.date_to,
  };

  if (filters.filter_type === "by_number") {
    params.phone_number = toE164(filters.phone_number);
  }

  if (filters.filter_type === "conversation") {
    params.number_a = toE164(filters.number_a);
    params.number_b = toE164(filters.number_b);
  }

  if (filters.filter_type === "owner_conversation") {
    params.owner_number = toE164(filters.owner_number);
    params.counterparty_number = toE164(filters.counterparty_number);
  }

  if (filters.filter_type === "by_entity") {
    params.entity_type = filters.entity_type;
    params.entity_id = filters.entity_id;
    params.side = filters.side;
  }

  return params;
}
